import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Badge, Modal, Form, Row, Col, Nav } from 'react-bootstrap';
import { Get, Post, Put } from '../../utils/httpProvider';
import { ANNUAL_JOB_APPROVAL_API, ANNUAL_JOB_API } from '../../utils/apiUrl';
import { toast } from 'react-toastify';
import Pagination from 'react-js-pagination';
import moment from 'moment';
import DatePicker from '@app/components/CustomDatePicker';
import { useSelector, useDispatch } from 'react-redux';
import { resetSearch } from '@store/reducers/search';

const AnnualJobApproval = () => {
    const [activeTab, setActiveTab] = useState('pending'); // 'pending' | 'approved'
    const [registrations, setRegistrations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState({ pageIndex: 1, pageSize: 10, keyword: '' });
    const [showOverdueOnly, setShowOverdueOnly] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    
    const dispatch = useDispatch();
    const valueSearch = useSelector((state) => state.search.valueSearch);

    useEffect(() => {
        return () => { dispatch(resetSearch()); };
    }, []);

    useEffect(() => {
        if (valueSearch != null && valueSearch !== filter.keyword) {
            setFilter(prev => ({ ...prev, keyword: valueSearch, pageIndex: 1 }));
        }
    }, [valueSearch]);

    useEffect(() => {
        setFilter(prev => ({ ...prev, isOverdueOnly: showOverdueOnly, pageIndex: 1 }));
    }, [showOverdueOnly]);

    useEffect(() => {
        fetchRegistrations();
    }, [filter.pageIndex, filter.pageSize, filter.keyword, filter.isOverdueOnly, activeTab]);

    // Modal Details
    const [showModal, setShowModal] = useState(false);
    const [selectedReg, setSelectedReg] = useState(null);

    // Modal Extend EndDate
    const [showExtendModal, setShowExtendModal] = useState(false);
    const [extendDate, setExtendDate] = useState('');
    const [extendingId, setExtendingId] = useState(null);

    // Modal Extend Detail EndDate
    const [showDetailExtendModal, setShowDetailExtendModal] = useState(false);
    const [detailExtendDate, setDetailExtendDate] = useState('');
    const [detailExtendingId, setDetailExtendingId] = useState(null);
    const [detailExtendingName, setDetailExtendingName] = useState('');

    // Admin Edit mode state
    const [isEditMode, setIsEditMode] = useState(false);
    const [editFormData, setEditFormData] = useState(null);
    const [availableJobs, setAvailableJobs] = useState([]);

    const fetchRegistrations = async () => {
        setLoading(true);
        try {
            const api = activeTab === 'pending'
                ? ANNUAL_JOB_APPROVAL_API.GET_ALL_PENDING
                : ANNUAL_JOB_APPROVAL_API.GET_ALL_APPROVED;
            const res = await Get(api, null, filter);
            if (res) {
                const rawData = res.Data ?? res.data ?? res;
                const rawList = Array.isArray(rawData) ? rawData : (rawData.$values || []);
                const normalized = rawList.map(reg => {
                    const rawDetails = reg.Details ?? reg.details;
                    const details = (rawDetails && rawDetails.$values ? rawDetails.$values : rawDetails) || [];
                    return {
                        id: reg.Id ?? reg.id,
                        fullName: reg.FullName ?? reg.fullName,
                        createdBy: reg.CreatedBy ?? reg.createdBy,
                        name: reg.Name ?? reg.name,
                        createdDate: reg.CreatedDate ?? reg.createdDate,
                        startDate: reg.StartDate ?? reg.startDate,
                        endDate: reg.EndDate ?? reg.endDate,
                        status: reg.Status ?? reg.status ?? 0,
                        details: details.map(d => ({
                            id: d.Id ?? d.id,
                            endDate: d.EndDate ?? d.endDate,
                            status: d.Status ?? d.status ?? 0
                        }))
                    };
                });
                setRegistrations(normalized);
                setTotalRecords(res.TotalRecords ?? res.totalRecords ?? 0);
            }
        } catch (error) {
            console.error('Error fetching registrations:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setShowOverdueOnly(false);
        setFilter(prev => ({ ...prev, pageIndex: 1, isOverdueOnly: false }));
    };

    const handleSearch = () => {
        setFilter({ ...filter, pageIndex: 1 });
        fetchRegistrations();
    };

    const handleViewDetail = async (id) => {
        try {
            const res = await Get(`${ANNUAL_JOB_APPROVAL_API.GET_BY_ID}/${id}`);
            if (res) {
                const reg = res.data ?? res.Data ?? res;
                const rawDetails = reg.Details ?? reg.details;
                const details = (rawDetails && rawDetails.$values ? rawDetails.$values : rawDetails) || [];
                const normalized = {
                    id: reg.Id ?? reg.id,
                    fullName: reg.FullName ?? reg.fullName,
                    createdBy: reg.CreatedBy ?? reg.createdBy,
                    name: reg.Name ?? reg.name,
                    startDate: reg.StartDate ?? reg.startDate,
                    endDate: reg.EndDate ?? reg.endDate,
                    status: reg.Status ?? reg.status ?? 0,
                    note: reg.Note ?? reg.note ?? '',
                    details: details.map(d => ({
                        id: d.Id ?? d.id,
                        annualJobId: d.AnnualJobId ?? d.annualJobId,
                        annualJobName: d.AnnualJobName ?? d.annualJobName ?? d.JobName ?? d.jobName,
                        annualJobBaseQuantity: d.AnnualJobBaseQuantity ?? d.annualJobBaseQuantity ?? d.BaseQuantity ?? d.baseQuantity ?? 1,
                        annualJobUnit: d.AnnualJobUnit ?? d.annualJobUnit ?? d.Unit ?? d.unit,
                        annualJobWeight: d.AnnualJobWeight ?? d.annualJobWeight ?? d.Weight ?? d.weight,
                        registeredKPI: d.RegisteredKPI ?? d.registeredKPI,
                        registeredPercent: d.RegisteredPercent ?? d.registeredPercent ?? 100,
                        registeredScore: d.RegisteredScore ?? d.registeredScore ?? 0,
                        currentKPI: d.CurrentKPI ?? d.currentKPI ?? 0,
                        endDate: d.EndDate ?? d.endDate,
                        status: d.Status ?? d.status ?? 0
                    }))
                };
                setSelectedReg(normalized);
                setShowModal(true);
            }
        } catch (error) {
            toast.error('Lỗi khi lấy chi tiết');
        }
    };

    const handleApprove = async (id) => {
        if (window.confirm('Xác nhận phê duyệt phiếu này?')) {
            try {
                await Post(`${ANNUAL_JOB_APPROVAL_API.APPROVE}/${id}`);
                toast.success('Đã phê duyệt thành công');
                setShowModal(false);
                fetchRegistrations();
            } catch (error) {
                toast.error('Lỗi phê duyệt');
            }
        }
    };

    const handleReject = async (id) => {
        if (window.confirm('Bạn muốn từ chối phiếu này? Người dùng sẽ phải làm lại.')) {
            try {
                await Post(`${ANNUAL_JOB_APPROVAL_API.REJECT}/${id}`);
                toast.success('Đã từ chối phiếu');
                setShowModal(false);
                fetchRegistrations();
            } catch (error) {
                toast.error('Lỗi từ chối');
            }
        }
    };

    const handleOpenExtendModal = (id, currentEndDate) => {
        setExtendingId(id);
        setExtendDate(moment(currentEndDate).format('YYYY-MM-DD'));
        setShowExtendModal(true);
    };

    const handleExtendEndDate = async () => {
        try {
            await Put(ANNUAL_JOB_APPROVAL_API.UPDATE_END_DATE, extendingId, extendDate);
            toast.success('Gia hạn thành công, các công việc đã hết hạn sẽ được mở khóa!');
            setShowExtendModal(false);
            if (showModal && selectedReg && selectedReg.id === extendingId) {
                handleViewDetail(extendingId); // reload detail
            }
            fetchRegistrations();
        } catch (error) {
            toast.error(error.response?.data || 'Lỗi gia hạn');
        }
    };

    const handleOpenDetailExtendModal = (detailId, currentEndDate, name) => {
        setDetailExtendingId(detailId);
        const dateVal = currentEndDate || selectedReg?.endDate;
        setDetailExtendDate(dateVal ? moment(dateVal).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'));
        setDetailExtendingName(name);
        setShowDetailExtendModal(true);
    };

    const handleExtendDetailEndDate = async () => {
        if (selectedReg && selectedReg.endDate && moment(detailExtendDate).isAfter(moment(selectedReg.endDate), 'day')) {
            toast.warning('Ngày gia hạn của công việc không được vượt quá ngày kết thúc của Phiếu đăng ký.');
            return;
        }
        try {
            await Put(ANNUAL_JOB_APPROVAL_API.UPDATE_DETAIL_END_DATE, detailExtendingId, detailExtendDate);
            toast.success('Gia hạn công việc thành công!');
            setShowDetailExtendModal(false);
            if (selectedReg) {
                handleViewDetail(selectedReg.id); // reload detail
            }
            fetchRegistrations();
        } catch (error) {
            toast.error(error.response?.data || 'Lỗi gia hạn công việc');
        }
    };

    const fetchAvailableJobs = async () => {
        try {
            const res = await Get(ANNUAL_JOB_API.GET_ALL_ACTIVE);
            if (res) {
                const dataObj = res.data ?? res.Data ?? res;
                const rawJobs = Array.isArray(dataObj) ? dataObj : (dataObj.$values || []);
                const normalized = rawJobs.map(j => ({
                    id: j.Id ?? j.id,
                    name: j.Name ?? j.name,
                    unit: j.Unit ?? j.unit,
                    weight: j.Weight ?? j.weight,
                    baseQuantity: j.BaseQuantity ?? j.baseQuantity
                }));
                setAvailableJobs(normalized);
            }
        } catch (error) {
            console.error('Error fetching jobs:', error);
        }
    };

    const handleEnterEditMode = () => {
        if (!selectedReg) return;
        setEditFormData({
            id: selectedReg.id,
            name: selectedReg.name,
            startDate: moment(selectedReg.startDate).format('YYYY-MM-DD'),
            endDate: moment(selectedReg.endDate).format('YYYY-MM-DD'),
            note: selectedReg.note || '',
            details: selectedReg.details.map(d => ({
                id: d.id,
                annualJobId: d.annualJobId,
                annualJobName: d.annualJobName,
                annualJobBaseQuantity: d.annualJobBaseQuantity,
                annualJobUnit: d.annualJobUnit,
                annualJobWeight: d.annualJobWeight,
                registeredKPI: d.registeredKPI,
                registeredPercent: d.registeredPercent,
                registeredScore: d.registeredScore,
                currentKPI: d.currentKPI,
                endDate: d.endDate ? moment(d.endDate).format('YYYY-MM-DD') : '',
                note: d.note || '',
                status: d.status
            }))
        });
        if (availableJobs.length === 0) {
            fetchAvailableJobs();
        }
        setIsEditMode(true);
    };

    const handleCancelEdit = () => {
        setIsEditMode(false);
        setEditFormData(null);
    };

    const handleEditAddJob = (e) => {
        const jobId = parseInt(e.target.value);
        if (!jobId) return;

        if (editFormData.details.find(d => d.annualJobId === jobId)) {
            toast.warning('Công việc này đã được thêm');
            e.target.value = '';
            return;
        }

        const job = availableJobs.find(j => j.id === jobId);
        if (job) {
            setEditFormData(prev => ({
                ...prev,
                details: [...prev.details, {
                    id: 0,
                    annualJobId: job.id,
                    annualJobName: job.name,
                    annualJobBaseQuantity: job.baseQuantity,
                    annualJobUnit: job.unit,
                    annualJobWeight: job.weight,
                    registeredKPI: 1,
                    registeredPercent: 100,
                    registeredScore: job.weight / (job.baseQuantity || 1),
                    currentKPI: 0,
                    endDate: '',
                    note: '',
                    status: 0
                }]
            }));
        }
        e.target.value = '';
    };

    const handleEditUpdateKPI = (jobId, value) => {
        const kpi = parseFloat(value) || 0;
        setEditFormData(prev => ({
            ...prev,
            details: prev.details.map(d => 
                d.annualJobId === jobId ? { ...d, registeredKPI: kpi } : d
            )
        }));
    };

    const handleEditUpdatePercent = (jobId, value) => {
        let percent = parseFloat(value);
        if (isNaN(percent)) percent = 0;
        if (percent < 0) percent = 0;
        if (percent > 100) percent = 100;
        setEditFormData(prev => ({
            ...prev,
            details: prev.details.map(d => 
                d.annualJobId === jobId ? { ...d, registeredPercent: percent } : d
            )
        }));
    };

    const handleEditUpdateNote = (jobId, value) => {
        setEditFormData(prev => ({
            ...prev,
            details: prev.details.map(d => 
                d.annualJobId === jobId ? { ...d, note: value } : d
            )
        }));
    };

    const handleEditUpdateEndDate = (jobId, value) => {
        setEditFormData(prev => ({
            ...prev,
            details: prev.details.map(d => 
                d.annualJobId === jobId ? { ...d, endDate: value } : d
            )
        }));
    };

    const handleEditRemoveJob = (jobId, currentKPI, jobName) => {
        if (currentKPI > 0) {
            toast.error("Không thể xóa công việc này vì nhân viên đã báo cáo tiến độ (KPI > 0) trên Kanban.");
            return;
        }
        if (window.confirm(`Bạn có chắc chắn muốn xóa công việc "${jobName || 'này'}" khỏi phiếu đăng ký không?`)) {
            setEditFormData(prev => ({
                ...prev,
                details: prev.details.filter(d => d.annualJobId !== jobId)
            }));
        }
    };

    const handleSaveEdit = async () => {
        if (!editFormData.name.trim()) {
            toast.warning('Vui lòng nhập tên phiếu đăng ký');
            return;
        }
        if (!editFormData.startDate || !editFormData.endDate) {
            toast.warning('Vui lòng chọn ngày bắt đầu và kết thúc');
            return;
        }
        if (editFormData.startDate > editFormData.endDate) {
            toast.warning('Ngày kết thúc phải sau ngày bắt đầu');
            return;
        }
        if (editFormData.details.length === 0) {
            toast.warning('Vui lòng chọn ít nhất 1 công việc');
            return;
        }
        const overdueDetail = editFormData.details.find(d => d.endDate && moment(d.endDate).isAfter(moment(editFormData.endDate), 'day'));
        if (overdueDetail) {
            toast.warning(`Hạn chót của công việc "${overdueDetail.annualJobName}" không được vượt quá ngày kết thúc của Phiếu đăng ký.`);
            return;
        }

        try {
            const payload = {
                name: editFormData.name,
                startDate: editFormData.startDate,
                endDate: editFormData.endDate,
                note: editFormData.note,
                details: editFormData.details.map(d => ({
                    id: d.id || null,
                    annualJobId: d.annualJobId,
                    registeredKPI: d.registeredKPI,
                    registeredPercent: d.registeredPercent,
                    note: d.note,
                    endDate: d.endDate || null
                }))
            };

            await Put(ANNUAL_JOB_APPROVAL_API.ADMIN_UPDATE, editFormData.id, payload);
            toast.success('Cập nhật phiếu đăng ký thành công!');
            setIsEditMode(false);
            setEditFormData(null);
            handleViewDetail(selectedReg.id);
            fetchRegistrations();
        } catch (error) {
            toast.error(error.response?.data || error.message || 'Lỗi khi cập nhật phiếu');
        }
    };

    return (
        <div className="container-fluid">
            <Card>
                <Card.Header>
                    <h3 className="card-title">Duyệt phiếu đăng ký công việc thường niên</h3>
                </Card.Header>
                <Card.Body style={{ minHeight: '600px', paddingBottom: '150px' }}>
                    
                    <Nav variant="tabs" activeKey={activeTab} onSelect={handleTabChange} className="mb-4">
                        <Nav.Item>
                            <Nav.Link eventKey="pending" className="fw-bold">
                                <i className="fas fa-clock mr-2 text-warning"></i> Chờ duyệt
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="approved" className="fw-bold">
                                <i className="fas fa-check-circle mr-2 text-success"></i> Đã duyệt
                            </Nav.Link>
                        </Nav.Item>
                    </Nav>

                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="d-flex align-items-center">
                            <label className="mr-2 mb-0">Hiển thị:</label>
                            <Form.Control
                                as="select"
                                size="sm"
                                style={{ width: '80px' }}
                                value={filter.pageSize}
                                onChange={(e) => {
                                    setFilter({ ...filter, pageSize: Number(e.target.value), pageIndex: 1 });
                                }}
                            >
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                            </Form.Control>
                            
                            <Form.Control
                                type="text"
                                size="sm"
                                placeholder="Nhập tên phiếu để tìm kiếm..."
                                style={{ width: '250px', marginLeft: '15px' }}
                                value={filter.keyword || ''}
                                onChange={(e) => setFilter({ ...filter, keyword: e.target.value, pageIndex: 1 })}
                            />

                            {activeTab === 'approved' && (
                                <Form.Check
                                    type="checkbox"
                                    id="showOverdueOnlyCheckbox"
                                    className="ml-4 d-flex align-items-center"
                                    label={
                                        <span className="fw-bold text-danger">
                                            <i className="fas fa-exclamation-triangle mr-1"></i> Chỉ hiện phiếu quá hạn
                                        </span>
                                    }
                                    checked={showOverdueOnly}
                                    onChange={(e) => setShowOverdueOnly(e.target.checked)}
                                />
                            )}
                        </div>
                    </div>

                    <div className="table-responsive" style={{ overflow: 'visible' }}>
                        <Table striped bordered hover style={{ fontSize: '15px', tableLayout: 'fixed' }}>
                            <colgroup>
                                <col style={{ width: '6%' }} />
                                <col style={{ width: '18%' }} />
                                <col style={{ width: '23%' }} />
                                <col style={{ width: '12%' }} />
                                <col style={{ width: '18%' }} />
                                <col style={{ width: '13%' }} />
                                <col style={{ width: '10%' }} />
                            </colgroup>
                            <thead className="bg-light">
                                <tr className="text-center">
                                    <th style={{ fontSize: '16px' }}>STT</th>
                                    <th style={{ fontSize: '16px' }}>Người đăng ký</th>
                                    <th style={{ fontSize: '16px' }}>Tên phiếu</th>
                                    <th style={{ fontSize: '16px' }}>Ngày tạo</th>
                                    <th style={{ fontSize: '16px' }}>Từ ngày - Đến ngày</th>
                                    <th style={{ fontSize: '16px' }}>Trạng thái</th>
                                    <th style={{ fontSize: '16px' }}>Thao tác</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan="7" className="text-center py-4">Đang tải...</td></tr>
                                ) : registrations.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="text-center py-4 text-muted font-italic">
                                            {activeTab === 'pending' ? 'Không có phiếu nào đang chờ duyệt' : 'Không có phiếu nào đã duyệt'}
                                        </td>
                                    </tr>
                                ) : (
                                    registrations.map((reg, index) => {
                                        const isOverdue = reg.status === 2 && (
                                            moment(reg.endDate).isBefore(moment(), 'day') ||
                                            (reg.details && reg.details.some(d => d.status !== 3 && moment(d.endDate || reg.endDate).isBefore(moment(), 'day')))
                                        );
                                        return (
                                            <tr key={reg.id}>
                                                <td className="text-center">{(filter.pageIndex - 1) * filter.pageSize + index + 1}</td>
                                                <td className="font-weight-bold">{reg.fullName || reg.createdBy}</td>
                                                <td>{reg.name}</td>
                                                <td className="text-center">{moment(reg.createdDate).format('DD/MM/YYYY')}</td>
                                                <td className="text-center">
                                                    <div>{moment(reg.startDate).format('DD/MM/YYYY')} - {moment(reg.endDate).format('DD/MM/YYYY')}</div>
                                                    {isOverdue && (
                                                        <Badge variant="danger" className="mt-1" style={{ fontSize: '11px' }}>
                                                            Quá hạn
                                                        </Badge>
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    {reg.status === 2 ? (
                                                        <Badge variant="success">Đã duyệt</Badge>
                                                    ) : reg.status === 1 ? (
                                                        <Badge variant="warning" text="dark">Chờ duyệt</Badge>
                                                    ) : (
                                                        <Badge variant="secondary">Khác</Badge>
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    <Button variant="info" size="sm" onClick={() => handleViewDetail(reg.id)}>
                                                        <i className="fas fa-eye mr-1"></i> Chi tiết
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </Table>
                    </div>

                    {totalRecords > 0 && (
                        <div className="d-flex justify-content-end mt-3">
                            <Pagination
                                activePage={filter.pageIndex}
                                itemsCountPerPage={filter.pageSize}
                                totalItemsCount={totalRecords}
                                pageRangeDisplayed={5}
                                onChange={(page) => setFilter({ ...filter, pageIndex: page })}
                                itemClass="page-item"
                                linkClass="page-link"
                            />
                        </div>
                    )}

                    {/* MODAL VIEW DETAIL */}
                    <Modal show={showModal} onHide={() => { setShowModal(false); setIsEditMode(false); setEditFormData(null); }} size="xl">
                        <Modal.Header closeButton>
                            <Modal.Title>{isEditMode ? 'Chỉnh sửa phiếu đăng ký (Admin)' : 'Chi tiết phiếu đăng ký'}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {selectedReg && (
                                isEditMode && editFormData ? (
                                    <>
                                        <Row className="mb-4">
                                            <Col md={12} className="mb-3">
                                                <Form.Group>
                                                    <Form.Label className="fw-bold">Tên phiếu đăng ký <span className="text-danger">*</span></Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        required
                                                        placeholder="VD: Đăng ký KPI năm học 2024 - 2025"
                                                        value={editFormData.name}
                                                        onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6} className="mb-3">
                                                <Form.Group>
                                                    <Form.Label className="fw-bold">Ngày bắt đầu <span className="text-danger">*</span></Form.Label>
                                                    <DatePicker 
                                                        className="form-control"
                                                        selected={editFormData.startDate ? new Date(editFormData.startDate) : null}
                                                        onChange={(date) => setEditFormData({ ...editFormData, startDate: date ? moment(date).format('YYYY-MM-DD') : '' })}
                                                        required
                                                        dateFormat="dd/MM/yyyy"
                                                        placeholderText="dd/MM/yyyy"
                                                        isClearable
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6} className="mb-3">
                                                <Form.Group>
                                                    <Form.Label className="fw-bold">Ngày kết thúc <span className="text-danger">*</span></Form.Label>
                                                    <DatePicker 
                                                        className="form-control"
                                                        selected={editFormData.endDate ? new Date(editFormData.endDate) : null}
                                                        onChange={(date) => setEditFormData({ ...editFormData, endDate: date ? moment(date).format('YYYY-MM-DD') : '' })}
                                                        required
                                                        dateFormat="dd/MM/yyyy"
                                                        placeholderText="dd/MM/yyyy"
                                                        isClearable
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label className="fw-bold">Ghi chú</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={2}
                                                        value={editFormData.note}
                                                        onChange={(e) => setEditFormData({ ...editFormData, note: e.target.value })}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <h6 className="fw-bold mb-3 text-secondary border-bottom pb-2">Danh sách công việc đăng ký</h6>
                                        
                                        <div className="mb-3">
                                            <Form.Control as="select" onChange={handleEditAddJob} defaultValue="">
                                                <option value="" disabled>-- Chọn công việc --</option>
                                                {availableJobs.map(job => (
                                                    <option key={job.id} value={job.id}>{job.name} (Khối lượng chuẩn: {job.baseQuantity} {job.unit} = {job.weight} điểm)</option>
                                                ))}
                                            </Form.Control>
                                        </div>

                                        <div className="table-responsive">
                                            <Table striped bordered hover className="align-middle" style={{ fontSize: '14px', tableLayout: 'fixed' }}>
                                                <colgroup>
                                                    <col style={{ width: '5%' }} />
                                                    <col style={{ width: '25%' }} />
                                                    <col style={{ width: '12%' }} />
                                                    <col style={{ width: '12%' }} />
                                                    <col style={{ width: '8%' }} />
                                                    <col style={{ width: '20%' }} />
                                                    <col style={{ width: '12%' }} />
                                                    <col style={{ width: '10%' }} />
                                                    <col style={{ width: '12%' }} />
                                                    <col style={{ width: '10%' }} />
                                                    <col style={{ width: '10%' }} />
                                                    <col style={{ width: '10%' }} />
                                                    <col style={{ width: '5%' }} />
                                                </colgroup>
                                                <thead className="bg-light text-center">
                                                    <tr>
                                                        <th>STT</th>
                                                        <th>Tên công việc</th>
                                                        <th>Hạn chót</th>
                                                        <th>Đơn vị tính</th>
                                                        <th>Quy đổi (Chuẩn/Điểm)</th>
                                                        <th>Số lượng ĐK</th>
                                                        <th>Tỉ lệ (%)</th>
                                                        <th>Điểm quy đổi</th>
                                                        <th>Thao tác</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {editFormData.details.length === 0 ? (
                                                        <tr>
                                                            <td colSpan="10" className="text-center text-muted py-4">Chưa có công việc nào được chọn</td>
                                                        </tr>
                                                    ) : (
                                                        editFormData.details.map((item, idx) => {
                                                            const baseQty = item.annualJobBaseQuantity || 1;
                                                            const percent = item.registeredPercent ?? 100;
                                                            const score = ((item.registeredKPI * item.annualJobWeight) / baseQty * (percent / 100)).toFixed(2);
                                                            const hasProgress = item.currentKPI > 0;
                                                            return (
                                                                <tr key={item.annualJobId}>
                                                                    <td className="text-center">{idx + 1}</td>
                                                                    <td className="font-weight-bold">{item.annualJobName}</td>
                                                                    <td>
                                                                        <Form.Control
                                                                            type="text"
                                                                            value={item.note || ''}
                                                                            onChange={(e) => handleEditUpdateNote(item.annualJobId, e.target.value)}
                                                                        />
                                                                    </td>
                                                                    <td>
                                                                        <DatePicker 
                                                                            className="form-control"
                                                                            selected={item.endDate ? new Date(item.endDate) : null}
                                                                            maxDate={editFormData.endDate ? new Date(editFormData.endDate) : null}
                                                                            onChange={(date) => handleEditUpdateEndDate(item.annualJobId, date ? moment(date).format('YYYY-MM-DD') : '')}
                                                                            dateFormat="dd/MM/yyyy"
                                                                            placeholderText="dd/MM/yyyy"
                                                                            isClearable
                                                                        />
                                                                    </td>
                                                                    <td className="text-center">{item.annualJobUnit}</td>
                                                                    <td className="text-center">{baseQty} {item.annualJobUnit} = {item.annualJobWeight} điểm</td>
                                                                    <td className="text-center">
                                                                        <Form.Control
                                                                            type="number"
                                                                            step="1"
                                                                            min="1"
                                                                            value={item.registeredKPI}
                                                                            onChange={(e) => handleEditUpdateKPI(item.annualJobId, e.target.value)}
                                                                            disabled={hasProgress}
                                                                        />
                                                                    </td>
                                                                    <td className="text-center">
                                                                        <Form.Control
                                                                            type="number"
                                                                            step="0.01"
                                                                            min="0"
                                                                            max="100"
                                                                            value={item.registeredPercent}
                                                                            onChange={(e) => handleEditUpdatePercent(item.annualJobId, e.target.value)}
                                                                            disabled={hasProgress}
                                                                        />
                                                                    </td>
                                                                    <td className="text-center fw-bold text-success">{score}</td>
                                                                    <td className="text-center">
                                                                        <Button variant="danger" size="sm" onClick={() => handleEditRemoveJob(item.annualJobId, item.currentKPI, item.annualJobName)}>
                                                                            <i className="fas fa-trash"></i>
                                                                        </Button>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })
                                                    )}
                                                    {editFormData.details.length > 0 && (
                                                        <tr className="bg-light fw-bold">
                                                            <td colSpan="8" className="text-end">Tổng điểm KPI dự kiến:</td>
                                                            <td className="text-success fs-5">
                                                                {editFormData.details.reduce((sum, item) => {
                                                                    const baseQty = item.annualJobBaseQuantity || 1;
                                                                    const percent = item.registeredPercent ?? 100;
                                                                    return sum + ((item.registeredKPI * item.annualJobWeight) / baseQty * (percent / 100));
                                                                }, 0).toFixed(2)}
                                                            </td>
                                                            <td></td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <Row className="mb-4">
                                            <Col md={6}>
                                                <p><strong>Người tạo:</strong> {selectedReg.fullName || selectedReg.createdBy}</p>
                                                <p><strong>Tên phiếu:</strong> {selectedReg.name}</p>
                                            </Col>
                                            <Col md={6}>
                                                <p>
                                                    <strong>Thời gian:</strong> {moment(selectedReg.startDate).format('DD/MM/YYYY')} - {moment(selectedReg.endDate).format('DD/MM/YYYY')}
                                                    {selectedReg.status === 2 && (
                                                        <Button variant="link" size="sm" onClick={() => handleOpenExtendModal(selectedReg.id, selectedReg.endDate)}>
                                                            <i className="fas fa-calendar-plus mr-1"></i> Gia hạn
                                                        </Button>
                                                    )}
                                                </p>
                                                <p><strong>Ghi chú:</strong> {selectedReg.note}</p>
                                            </Col>
                                        </Row>

                                        <h6>Danh sách công việc</h6>
                                        <Table bordered hover style={{ fontSize: '14px', tableLayout: 'fixed' }}>
                                            <colgroup>
                                                <col style={{ width: '5%' }} />
                                                <col style={{ width: selectedReg.status === 2 ? '25%' : '30%' }} />
                                                <col style={{ width: '22%' }} />
                                                <col style={{ width: '10%' }} />
                                                <col style={{ width: '8%' }} />
                                                <col style={{ width: '9%' }} />
                                                <col style={{ width: '13%' }} />
                                                {selectedReg.status === 2 && <col style={{ width: '10%' }} />}
                                            </colgroup>
                                            <thead className="bg-light">
                                                <tr className="text-center">
                                                    <th>STT</th>
                                                    <th>Tên công việc</th>
                                                    <th>Trọng số / Định mức</th>
                                                    <th>Khối lượng ĐK</th>
                                                    <th>Tỉ lệ (%)</th>
                                                    <th>Điểm quy đổi</th>
                                                    <th>Hạn chót công việc</th>
                                                    {selectedReg.status === 2 && <th>Thao tác</th>}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedReg.details && selectedReg.details.map((d, i) => {
                                                    const detailEndDate = d.endDate || selectedReg.endDate;
                                                    const isDetailOverdue = selectedReg.status === 2 && d.status !== 3 && detailEndDate && moment(detailEndDate).isBefore(moment(), 'day');
                                                    return (
                                                        <tr key={d.id}>
                                                            <td className="text-center">{i + 1}</td>
                                                            <td>{d.annualJobName}</td>
                                                            <td>{d.annualJobBaseQuantity} {d.annualJobUnit} = {d.annualJobWeight} điểm</td>
                                                            <td className="text-center fw-bold">{d.registeredKPI}</td>
                                                            <td className="text-center text-info fw-bold">{d.registeredPercent}%</td>
                                                            <td className="text-center text-success fw-bold">{d.registeredScore.toFixed(2)}</td>
                                                            <td className="text-center">
                                                                <div>
                                                                    {d.endDate ? (
                                                                        <span className="text-primary fw-bold">
                                                                            {moment(d.endDate).format('DD/MM/YYYY')}
                                                                        </span>
                                                                    ) : (
                                                                        <span className="text-muted">Theo hạn tổng</span>
                                                                    )}
                                                                </div>
                                                                {isDetailOverdue && (
                                                                    <Badge variant="danger" className="mt-1" style={{ fontSize: '11px' }}>
                                                                        Quá hạn
                                                                    </Badge>
                                                                )}
                                                            </td>
                                                            {selectedReg.status === 2 && (
                                                                <td className="text-center">
                                                                    <Button 
                                                                        variant="outline-primary" 
                                                                        size="sm" 
                                                                        className="text-nowrap"
                                                                        onClick={() => handleOpenDetailExtendModal(d.id, d.endDate, d.annualJobName)}
                                                                    >
                                                                        <i className="fas fa-calendar-plus"></i> Gia hạn
                                                                    </Button>
                                                                </td>
                                                            )}
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    </>
                                )
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            {isEditMode ? (
                                <>
                                    <Button variant="secondary" onClick={handleCancelEdit}>Hủy bỏ</Button>
                                    <Button variant="success" onClick={handleSaveEdit}>Lưu thay đổi</Button>
                                </>
                            ) : (
                                <>
                                    {selectedReg && selectedReg.status === 1 && (
                                        <>
                                            <Button variant="danger" onClick={() => handleReject(selectedReg.id)}>Từ chối</Button>
                                            <Button variant="success" onClick={() => handleApprove(selectedReg.id)}>Phê duyệt</Button>
                                        </>
                                    )}
                                    {selectedReg && selectedReg.status === 2 && !moment(selectedReg.endDate).isBefore(moment(), 'day') && (
                                        <Button variant="warning" className="text-dark fw-bold" onClick={handleEnterEditMode}>
                                            <i className="fas fa-edit mr-1"></i> Sửa phiếu
                                        </Button>
                                    )}
                                    <Button variant="secondary" onClick={() => { setShowModal(false); setIsEditMode(false); setEditFormData(null); }}>Đóng</Button>
                                </>
                            )}
                        </Modal.Footer>
                    </Modal>

                    {/* MODAL EXTEND DATE */}
                    <Modal show={showExtendModal} onHide={() => setShowExtendModal(false)}>
                        <Modal.Header closeButton>
                            <Modal.Title>Gia hạn thời gian phiếu</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Form.Group>
                                <Form.Label>Ngày kết thúc mới</Form.Label>
                                <DatePicker 
                                    className="form-control"
                                    selected={extendDate ? new Date(extendDate) : null}
                                    onChange={(date) => setExtendDate(date ? moment(date).format('YYYY-MM-DD') : '')}
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="dd/MM/yyyy"
                                    isClearable
                                />
                                <Form.Text className="text-muted">
                                    Những công việc đã bị chuyển sang trạng thái "Không hoàn thành / Quá hạn" sẽ được mở khóa lại.
                                </Form.Text>
                            </Form.Group>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowExtendModal(false)}>Hủy</Button>
                            <Button variant="primary" onClick={handleExtendEndDate}>Xác nhận gia hạn</Button>
                        </Modal.Footer>
                    </Modal>

                    {/* MODAL EXTEND DETAIL DATE */}
                    <Modal show={showDetailExtendModal} onHide={() => setShowDetailExtendModal(false)}>
                        <Modal.Header closeButton>
                            <Modal.Title>Gia hạn thời gian công việc</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Form.Group className="mb-3">
                                <Form.Label>Tên công việc</Form.Label>
                                <Form.Control
                                    type="text"
                                    plaintext
                                    readOnly
                                    className="fw-bold text-primary"
                                    value={detailExtendingName}
                                />
                            </Form.Group>
                            <Form.Group>
                                <Form.Label>Ngày kết thúc mới</Form.Label>
                                <DatePicker 
                                    className="form-control"
                                    selected={detailExtendDate ? new Date(detailExtendDate) : null}
                                    maxDate={selectedReg?.endDate ? new Date(selectedReg.endDate) : null}
                                    onChange={(date) => setDetailExtendDate(date ? moment(date).format('YYYY-MM-DD') : '')}
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="dd/MM/yyyy"
                                    isClearable
                                />
                                <Form.Text className="text-muted">
                                    Gia hạn thời hạn riêng cho công việc này. Nếu công việc đang bị quá hạn sẽ được mở khóa lại.
                                </Form.Text>
                            </Form.Group>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowDetailExtendModal(false)}>Hủy</Button>
                            <Button variant="primary" onClick={handleExtendDetailEndDate}>Xác nhận gia hạn</Button>
                        </Modal.Footer>
                    </Modal>

                </Card.Body>
            </Card>
        </div>
    );
};

export default AnnualJobApproval;
